<template>
  <div class="order-list-pills">
    <h2 class="order-list-pills__title">
      {{ $t("page.order.submit.table.header.main") }}
    </h2>
    <div class="order-list-pills__controls">
      <ul class="sort-list">
        <li :class="{ current: !currentTabIndex }" @click="onTabChange(null)">
          {{ $t("page.order.submit.tab.all.header") }} ({{ allOrdersAmount }})
        </li>
        <li
          v-for="type in showDeliveryTypesTabs"
          :key="'tab-' + type.id"
          :class="{ current: currentTabIndex === type.id }"
          @click="onTabChange(type.id)"
        >
          {{ $t(type.deliveryTypeTranslationKey).replace(/ .*/, "") }}
          ({{ getAmountOfCanistersByDeliveryTypeId(orderListOnProduction, type.id) }})
        </li>
        <li
          v-if="!!onHoldAmount"
          @click="onTabChange(ON_HOLD_LIST)"
          :class="{ current: currentTabIndex === ON_HOLD_LIST }"
        >
          {{ $t("page.order.submit.tab.onhold.header") }} ({{ onHoldAmount }})
        </li>
      </ul>
      <div class="search-input">
        <input
          id="search-input"
          type="serach"
          :placeholder="'Search for ' + currentMedKeyTypeName + ', medicine name...'"
          v-model="orderSearchPhrase"
        />
      </div>
    </div>
    <order-submit-pills-table
      :orders="onProductionTabList"
      :delivery-types="deliveryTypes"
      :free-contingent="freeContingent"
      :is-filter="!!orderSearchPhrase"
      :freeItemsLeft="freeItemsLeft"
      :freeItemsTotal="freeItemsTotal"
      deliveryInfo="true"
      v-if="currentTabIndex !== ON_HOLD_LIST"
    >
      <div class="order-list-pills__table-info">
        <h2>{{ $t("page.order.submit.table.onproduction.header") }}</h2>
        <div class="right">
          <span>
            <strong>{{ onProductionAmount }}</strong>
            {{ $t("page.order.submit.table.header.canisters") }}
          </span>
          <span v-for="type in deliveryTypes" :key="type.id">
            {{ $t(type.deliveryTypeTranslationKey) }}:
            <strong>
              {{ formatDate(type.estimatedDeliveryDate, basicData.checkoutDisplayMode === "us") }}
            </strong>
          </span>
        </div>
      </div>
    </order-submit-pills-table>

    <order-submit-pills-table
      id="on-hold-order-pills"
      :orders="onHoldTabList"
      :delivery-types="deliveryTypes"
      :is-on-hold-type="true"
      :is-filter="!!orderSearchPhrase"
      v-if="orderListOnHold && orderListOnHold.length > 0 && (currentTabIndex === ON_HOLD_LIST || !currentTabIndex)"
    >
      <div class="order-list-pills__table-info">
        <h2>
          {{ $t("page.order.submit.table.onhold.header") }}
          <div class="icon-question-blue">
            <img src="~@/assets/icons/icon-question-blue.svg" @click="openTableTooltip" />
            <div class="popup-info" :class="{ open: tableTooltip }">
              <span class="popup-info__close" @click="closeTableTooltip">
                <img src="~@/assets/icons/icon-cancel-black.svg" />
              </span>
              <img src="~@/assets/icons/icon-table-popup.svg" />
              <div class="popup-info__text">
                <div class="popup-info__title">{{ $t("page.order.submit.rotorunderconstruction.popup.header") }}</div>
                <p class="popup-info__desc">
                  {{ $t("page.order.submit.rotorunderconstruction.popup.description") }}
                </p>
              </div>
            </div>
          </div>
        </h2>
        <div class="right">
          <span>
            <strong>{{ onHoldAmount }}</strong>
            {{ $t("page.order.submit.table.header.canisters") }}
          </span>
          <span v-if="onHoldDeliveryDetails" id="confirmation-date">
            {{ $t("deliverytype.expectedconfirmation") }}:
            <strong>{{ estimatedOnHoldConfirmationDate }}</strong>
          </span>
          <span v-if="onHoldDeliveryDetails">
            {{ $t(onHoldDeliveryDetails.deliveryTypeTranslationKey) }}:
            <strong>
              {{ formatDate(onHoldDeliveryDetails.estimatedDeliveryDate, basicData.checkoutDisplayMode === "us") }}
            </strong>
          </span>
        </div>
      </div>
    </order-submit-pills-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as DateUtils from "../../utils/DateUtils";
import OrderSubmitPillsTable from "./order-submit-pills-table";
import moment from "moment";

export default {
  name: "VOrderSubmitPills",
  components: {
    OrderSubmitPillsTable,
  },
  props: {
    freeItemsLeft: {
      type: Number,
      default: null,
    },
    freeItemsTotal: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      ON_HOLD_LIST: "ON_HOLD_LIST",
      orderSearchPhrase: null,
      currentTabIndex: null,
      tableTooltip: false,
    };
  },
  computed: {
    ...mapGetters("order", [
      "basicData",
      "allOrdersAmount",
      "onProductionAmount",
      "onHoldAmount",
      "onHoldDeliveryDetails",
      "orderListOnProduction",
      "orderListOnHold",
      "deliveryTypes",
      "freeContingent",
    ]),
    ...mapGetters("medKey", ["currentMedKeyTypeName"]),
    showDeliveryTypesTabs() {
      return this.deliveryTypes
        ? this.deliveryTypes.filter((d) => this.orderListOnProduction.find((o) => o.deliveryTypeId === d.id))
        : [];
    },
    estimatedOnHoldConfirmationDate() {
      return this.formatDate(
        this.onHoldDeliveryDetails.estimatedOnHoldConfirmationDate,
        this.basicData.checkoutDisplayMode === "us"
      );
    },
    onProductionTabList() {
      const tab = !this.currentTabIndex
        ? this.orderListOnProduction
        : this.orderListOnProduction.filter((o) => o.deliveryTypeId === this.currentTabIndex);
      return this.orderSearchPhrase ? tab.filter(this.filterOrders) : tab;
    },
    onHoldTabList() {
      return this.orderSearchPhrase ? this.orderListOnHold.filter(this.filterOrders) : this.orderListOnHold;
    },
  },
  methods: {
    printEstimatedDate: DateUtils.printEstimatedDate,
    formatDate(value, isUS = false) {
      const date = moment(value);

      return isUS ? date.format("MM/DD/YYYY") : date.format("DD.MM.YYYY");
    },
    getAmountOfCanistersByDeliveryTypeId(orders, typeId) {
      return orders.filter((o) => o.deliveryTypeId === typeId).reduce((prev, cur) => prev + cur.amountOfCanisters, 0);
    },
    onTabChange(index) {
      this.currentTabIndex = index;
    },
    filterOrders(o) {
      return (
        o.medKey.toLowerCase().includes(this.orderSearchPhrase.toLowerCase()) ||
        o.name.toLowerCase().includes(this.orderSearchPhrase.toLowerCase())
      );
    },
    closeTableTooltip() {
      this.tableTooltip = false;
    },
    openTableTooltip() {
      this.tableTooltip = !this.tableTooltip;
    },
  },
};
</script>

<style lang="scss">
.order-list-pills {
  margin: 32px 0 0 0;
  &__title {
    font-size: 24px;
    line-height: 26px;
    margin: 0 0 0 8px;
    letter-spacing: 0.8px;
  }
  &__controls {
    display: flex;
    justify-content: space-between;
    margin: 11px 0 62px 0;
  }
  &__table-info {
    display: flex;
    align-items: flex-end;
    margin: 39px 19px 14px 33px;
    h2 {
      font-size: 24px;
      line-height: 26px;
      letter-spacing: 0.9px;
      @media all and (max-width: 1200px) {
        font-size: 20px;
        line-height: 20px;
        letter-spacing: 0.3px;
      }
      img {
        // margin: -4px 0 0 5px;
        // display: inline-block;
        // vertical-align: middle;
        cursor: pointer;
      }
      .icon-question-blue {
        margin: -4px 0 0 5px;
        display: inline-block;
        vertical-align: middle;
        position: relative;
      }
    }
    .right {
      margin-left: auto;
      span {
        font-size: 13px;
        margin: 0 0 0 25px;
        letter-spacing: 0.55px;
        @media all and (max-width: 1200px) {
          font-size: 12px;
          letter-spacing: 0;
          margin: 0 0 0 10px;
        }
        strong {
          margin: 0;
          span {
            margin: 0;
          }
          font-weight: bold;
        }
      }
    }
  }
  ::v-deep .options {
    width: 100%;
  }
}
.sort-list {
  margin: 12px 0 0 8px;
  padding: 0;
  list-style: none;
  display: flex;
  li {
    margin: 0 33px 0 0;
    color: #999999;
    font-size: 16px;
    border-bottom: 4px solid transparent;
    padding: 0 0 19px 0;
    cursor: pointer;
    &.current {
      color: #333;
      border-color: #d15421;
    }
  }
}
.search-input {
  width: 470px;
  input {
    background: #fdfdfd url("~@/assets/icons/icon-search.svg") 18px center;
    background-repeat: no-repeat;
    border: 1px solid #ececec;
    border-radius: 100px;
    padding: 0 0 0 47px;
    width: 100%;
    height: 42px;
    outline: none;
    &::placeholder {
      color: #999999;
    }
  }
}

.popup-info {
  position: absolute;
  z-index: 10;
  left: 100%;
  bottom: 100%;
  background: #fff;
  box-shadow: 0px 12px 34px rgba(153, 153, 153, 0.5);
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  width: 510px;
  padding: 31px 40px 29px 33px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  &.open {
    opacity: 1;
    visibility: visible;
  }
  &__text {
    width: calc(100% - 142px);
  }
  &__title {
    font-size: 16px;
    line-height: 17px;
    margin: 10px 0 9px 0;
    color: #000000;
    letter-spacing: 0;
  }
  &__desc {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0.433333px;
    color: #000000;
    strong {
      font-weight: bold;
    }
  }
  &__close {
    position: absolute;
    right: 24px;
    top: 12px;
    cursor: pointer;
    img {
      width: 12px;
    }
  }
}
</style>
